import type { HomeSettings } from "building-types/settings/homeSettings";
import { SettingsType } from "building-types/settings/settingsType";
import type { ProvenanceCriteriaCollectionInput, ProvenanceCriterionInput } from "./criteriaInputTypes";
import { CriteriaType } from "./criteriaTypes";
import type { ProvenanceCriteriaCollection, ProvenanceCriterion } from "./provenanceTypes";
import { getSettingsCriterion } from "./settingsCriteriaTools";

export const AutoExteriorDimensionProvenanceCriteria: ProvenanceCriterionInput[] = [
  {
    type: "SETTINGS_CRITERION",
    path: "planset.exteriorDimensionType", // a type-safe dot notation path
  },
  {
    type: "SETTINGS_CRITERION",
    path: "planset.exteriorDimensionDatum",
  },
];

export const AutoInteriorDimensionProvenanceCriteria: ProvenanceCriterionInput[] = [
  {
    type: "SETTINGS_CRITERION",
    path: "planset.interiorDimensionType",
  },
];

export const AutoFoundationExteriorDimensionProvenanceCriteria: ProvenanceCriterionInput[] = [
  // NONE FOR NOW
];

export const AutoFoundationInteriorDimensionProvenanceCriteria: ProvenanceCriterionInput[] = [
  // NONE FOR NOW
];

const defaultProvenanceCriteriaInput: ProvenanceCriteriaCollectionInput = {
  [CriteriaType.EXTERIOR_AUTO_DIMENSION]: AutoExteriorDimensionProvenanceCriteria,
  [CriteriaType.INTERIOR_AUTO_DIMENSION]: AutoInteriorDimensionProvenanceCriteria,
  [CriteriaType.FOUNDATION_EXTERIOR_AUTO_DIMENSION]: AutoFoundationExteriorDimensionProvenanceCriteria,
  [CriteriaType.FOUNDATION_INTERIOR_AUTO_DIMENSION]: AutoFoundationInteriorDimensionProvenanceCriteria,
};

export function makeProvenanceCriteria(
  homeSettings: HomeSettings,
  inputs: ProvenanceCriterionInput[],
): ProvenanceCriterion[] {
  const output: ProvenanceCriterion[] = [];
  for (const criterion of inputs) {
    switch (criterion.type) {
      // other types of criteria could be added here
      case "SETTINGS_CRITERION": {
        const settingCriterion = getSettingsCriterion(homeSettings, criterion.path);
        if (!settingCriterion) {
          continue;
        }
        output.push(settingCriterion);
        break;
      }
    }
  }
  return output;
}

export function makeProvenanceCriteriaCollection(
  homeSettings: HomeSettings,
  inputs: ProvenanceCriteriaCollectionInput = defaultProvenanceCriteriaInput,
): ProvenanceCriteriaCollection {
  const criteria: { [key in CriteriaType]: ProvenanceCriterion[] } = {
    [CriteriaType.EXTERIOR_AUTO_DIMENSION]: [],
    [CriteriaType.INTERIOR_AUTO_DIMENSION]: [],
    [CriteriaType.FOUNDATION_EXTERIOR_AUTO_DIMENSION]: [],
    [CriteriaType.FOUNDATION_INTERIOR_AUTO_DIMENSION]: [],
  };
  for (const key in inputs) {
    const input = inputs[key] as ProvenanceCriterionInput[];
    criteria[key] = makeProvenanceCriteria(homeSettings, input);
  }

  return criteria;
}

export function getProvenanceCriteria(
  collection: ProvenanceCriteriaCollection,
  type: CriteriaType,
): ProvenanceCriterion[] {
  if (!collection) {
    return [] as ProvenanceCriterion[];
  }

  const result = collection[type];
  if (!result) {
    return [] as ProvenanceCriterion[];
  }
  return result;
}

export function isProvenanceCriteriaInteriorDimByRoomGroup(criteria: ProvenanceCriterion): boolean {
  if (
    criteria.type === "SETTINGS_CRITERION" &&
    criteria.settingsType === SettingsType.PLAN_SETTINGS &&
    criteria.path === "planset.interiorDimensionType" &&
    criteria.value === "BY_ROOM_GROUP"
  ) {
    return true;
  }
  return false;
}

export function isProvenanceCriteriaInteriorDimByString(criteria: ProvenanceCriterion): boolean {
  if (
    criteria.type === "SETTINGS_CRITERION" &&
    criteria.settingsType === SettingsType.PLAN_SETTINGS &&
    criteria.path === "planset.interiorDimensionType" &&
    criteria.value === "BY_STRING"
  ) {
    return true;
  }
  return false;
}
